import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, FadeUp } from "@/components/site/Reveal";
import { useSiteContent } from "@/context/ContentContext";

function Row({ cat, i }) {
  const flip = i % 2 === 1;
  return (
    <div data-testid={`category-${cat.id}`} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
      <div className={`lg:col-span-7 ${flip ? "lg:order-2" : "lg:order-1"}`}>
        <motion.div
          initial={{ clipPath: "inset(12% 12% 12% 12%)", opacity: 0.5 }}
          whileInView={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
          viewport={{ once: true, margin: "-12%" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden group aspect-[16/11]"
        >
          <img
            src={cat.image}
            alt={cat.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 ring-1 ring-inset ring-[#0f172a]/10" />
        </motion.div>
      </div>

      <div className={`lg:col-span-5 ${flip ? "lg:order-1" : "lg:order-2"}`}>
        <p className="overline !text-[#9c7a4d] mb-4">{cat.tag}</p>
        <h3 className="font-serif font-light text-[#0f172a] text-3xl md:text-5xl leading-tight tracking-tight mb-6">
          <Reveal>{cat.title}</Reveal>
        </h3>
        <FadeUp delay={0.05}>
          <p className="text-[#475569] text-base md:text-lg font-light leading-relaxed mb-8">{cat.body}</p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <Link
            to={`/services/${cat.id}`}
            data-testid={`category-cta-${cat.id}`}
            className="group inline-flex items-center gap-2 text-[#0f172a] text-sm uppercase tracking-[0.2em] border-b border-[#0f172a]/20 hover:border-[#9c7a4d] pb-1 transition-colors"
          >
            Explore {cat.title}
            <ArrowUpRight className="h-4 w-4 text-[#9c7a4d] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </FadeUp>
      </div>
    </div>
  );
}

export default function Categories() {
  const { categories: CATEGORIES } = useSiteContent();
  return (
    <section id="specialities" data-testid="categories-section" className="relative bg-[#f8fafc] py-24 md:py-32">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="max-w-3xl mb-20 md:mb-28">
          <p className="overline !text-[#9c7a4d] mb-6">What We Specialise In</p>
          <h2 className="font-serif font-light text-[#0f172a] text-4xl sm:text-5xl lg:text-7xl leading-[0.98] tracking-tight">
            <Reveal>Eight disciplines,</Reveal>
            <Reveal delay={0.08}>one standard.</Reveal>
          </h2>
        </div>

        <div className="space-y-28 md:space-y-40">
          {CATEGORIES.map((cat, i) => (
            <Row key={cat.id} cat={cat} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
